import React, { Component, Context, createContext, JSX } from 'react';
import { read_cookie } from "sfcookies";
import { hostUrl, UseUrlManagerCleanUrl } from "../../utils/url-manager";
import { ChatFormBridge, ChatFormBridgeContext, ChatScreen, describeScreen } from "./ChatFormBridge";
import { daiveChatModels, defaultDaiveChatModel } from "../../../defaults/defaults";
import { withCard } from './transcript-placement';


/**
 * What HQ-API hands back when it picks an Operator for this admin to talk to.
 *
 * HQ used to fetch `operators?name=Daive` itself and read these off the serialised entity. That
 * pinned the chat to one named row, and the Operator collection is ROLE_SUPER_ADMIN/ROLE_OWNER
 * only, so every other admin silently got no chat at all.
 */
export interface ChatConnection {
    host: string;
    /**
     * Signed by HQ-API, good for one connection for about a minute. It replaces the standing
     * socket token that used to be handed to the browser: that one was readable from the network
     * tab, worked forever, and said nothing about who was connecting, so anyone holding it could
     * name any connector and be given another admin's conversation.
     */
    ticket: string;
    botName: string;
    connectorId: string;
    userName: string;
}

/** A tool call the assistant made, shown inline in the transcript as it happens. */
export interface ChatToolActivity {
    id: string;
    name: string;
    label: string;
    status: 'running' | 'done';
    summary?: string;
}

/** A map placed in the conversation for an address that was looked up. */
export interface ChatMapCard {
    label: string;
    embedUrl: string;
    hqUrl: string;
}

/** An offer to open a record in HQ. The user clicks it; nothing moves on its own. */
export interface ChatNavigation {
    label: string;
    url: string;
    resource: string;
}

export interface ChatMessage {
    message: string;
    author: string;
    type: 'operator' | 'user' | 'tool' | 'navigate' | 'map' | 'proposal' | 'conversion' | 'report';
    /** True while deltas are still arriving for this message. */
    streaming?: boolean;
    /** Set only on entries of type 'tool'. */
    tool?: ChatToolActivity;
    /** Set only on entries of type 'navigate'. */
    navigation?: ChatNavigation;
    /** Set only on entries of type 'map'. */
    map?: ChatMapCard;
    /** Set only on entries of type 'proposal'. */
    proposal?: ChatProposal;
    /** Set only on entries of type 'conversion'. */
    conversion?: ChatConversion;
    /** Set only on entries of type 'report'. */
    report?: ChatReport;
    /**
     * When this was first put on screen, if it is being shown again.
     *
     * HQ-11. A resumed conversation used to come back as text: every map, chip, button and
     * proposal is drawn during a tool call, and tool traffic is stripped before a conversation
     * is stored. They are recorded separately now and replayed - but a map is a picture of
     * where a record was at the time, so a replayed one has to say when it was taken and must
     * not offer to act on it. Set means "this is a record, not a live view".
     */
    capturedAt?: number;
}

/**
 * A PDF the assistant has written, waiting to be downloaded.
 *
 * `pdf` is the whole file, base64 encoded, held in memory. Nothing writes it to disk, to
 * localStorage or to Redis, so it is gone on reload and a resumed conversation comes back with
 * the words but not the button.
 */
export interface ChatReport {
    title: string;
    filename: string;
    bytes: number;
    pdf: string;
}

/** A value the assistant has offered for a field on the screen behind the panel. */
export interface ChatProposal {
    field: string;
    value: string;
    why: string;
    resource: string;
    recordId: string | number | null;
    was: string;
}

/**
 * A sales contact the assistant has offered to turn into a customer.
 *
 * Larger than a `ChatProposal` and the same bargain: an offer, never a write. Approving it is an
 * ordinary authenticated POST from this browser to HQ-API carrying the admin's own token, so the
 * ACL applies as it does to any HQ screen and the assistant gains no write path. See
 * `ConversionCard`.
 */
export interface ChatConversion {
    /** Where the contact came from, so the card can cite it and HQ-API can close it. */
    sourceResource: string;
    sourceId: number;
    sourceLabel: string;
    sourceHqUrl: string;
    /** One sentence from the assistant on why this contact is worth converting. */
    why: string;
    customer: {
        email: string;
        firstName: string;
        lastName: string;
        telephone: string;
        jobTitle: string;
    };
    account: {
        type: string;
        status: string;
    };
}

/** A transcript the Operator sent back, stamped so the same words twice still arrive twice. */
export interface ChatTranscript {
    text: string;
    at: number;
}

/** Frames the Operator sends. */
interface OperatorFrame {
    type?: 'status' | 'token' | 'done' | 'error' | 'tool' | 'navigate' | 'map' | 'closed' | 'transcript' | 'proposal' | 'conversion' | 'forgotten' | 'history' | 'report';
    status?: string;
    response?: string;
    id?: string;
    name?: string;
    label?: string;
    summary?: string;
    url?: string;
    resource?: string;
    embedUrl?: string;
    hqUrl?: string;
    text?: string;
    error?: string;
    field?: string;
    value?: string;
    why?: string;
    recordId?: string | number | null;
    was?: string;
    /** On a 'conversion' frame: the contact, and what it would become. */
    sourceResource?: string;
    sourceId?: number;
    sourceLabel?: string;
    customer?: ChatConversion['customer'];
    account?: ChatConversion['account'];
    /** Set on a frame being shown again rather than for the first time. See `capturedAt`. */
    replayed?: boolean;
    /** On a replayed frame: unix seconds, when it was first drawn. */
    capturedAt?: number;
    /** On a replayed frame: how many turns had been said when it was first drawn. */
    afterTurn?: number;
    /** On a 'report' frame: the whole PDF, base64 encoded, with its name and size. */
    title?: string;
    filename?: string;
    bytes?: number;
    pdf?: string;
    messages?: { author: string, message: string, type: 'user' | 'operator' }[];
}

/**
 * What the assistant is doing between a question being sent and the reply being finished.
 *
 * Separate from `thinking`, which is a single boolean covering both and is what disables the
 * composer. This is what the transcript shows, and the two halves do not look alike: waiting for
 * a model that has not said anything yet is dead air worth marking, and a reply arriving word by
 * word marks itself.
 */
export type ChatActivity = '' | 'thinking' | 'answering';

interface ChatBotState {
    connection: ChatConnection | null;
    chatMessages: ChatMessage[];
    websocket: WebSocket | null;
    thinking: boolean;
    /** Shown in the transcript. See ChatActivity. */
    activity: ChatActivity;
    /** True from pressing Stop until Daive closes the reply with `done`. */
    stopping: boolean;
    loadingOperator: boolean;
    showChatPanel: boolean;
    errorMessage: string;
    retries: number;
    /** True while a recording is with the Operator. */
    transcribing: boolean;
    transcript: ChatTranscript | null;
    /** Bumped by a reset. The composer is keyed on it, so it is rebuilt as a fresh one. */
    conversations: number;
    /** Whether opening the panel brings the last conversation back. */
    resumeConversation: boolean;
    /** How many finished conversations the assistant may be asked about, 0 to the ceiling. */
    recallConversations: number;
    /** Which model answers. An LLM selector from `daiveChatModels`. */
    chatModel: string;
}

/**
 * How long the panel may sit untouched before it gives its connection back.
 *
 * Slightly under the Operator's own twenty minute timeout, so the client lets go first and the
 * user is told why, rather than discovering it by sending into a socket that is already gone.
 */
const IDLE_TIMEOUT_MS: number = 18 * 60 * 1000;

/** How many times a dropped socket is retried before the drawer gives up and says so. */
const MAX_RETRIES: number = 5;
/** Offered on every connection and accepted by Daive, so the ticket name is never echoed back. */
const CHAT_SUBPROTOCOL: string = 'daive.v1';

/** Carries the ticket. Must match TICKET_SUBPROTOCOL_PREFIX in Daive's chatbot_route.py. */
const CHAT_TICKET_SUBPROTOCOL_PREFIX: string = 'daive.ticket.';

/** Milliseconds between retries, multiplied by the attempt number. */
const RETRY_BACKOFF_MS: number = 1000;

/** Where the "carry the conversation over" toggle is remembered, beside the panel's position. */
const RESUME_STORAGE_KEY: string = 'chatPanelResume';

/** Where "how many past conversations does it remember" is remembered, beside the other two. */
const RECALL_STORAGE_KEY: string = 'chatPanelRecall';

/** Where the chosen model is remembered, beside the other three. */
const MODEL_STORAGE_KEY: string = 'chatPanelModel';

/**
 * The most past conversations the assistant will ever hold on to.
 *
 * **Must match `MAX_ARCHIVED_CONVERSATIONS` in Daive's `memory.py`**, which is the half that
 * actually enforces it: this number decides what the menu offers, and Daive clamps whatever
 * arrives regardless, because the setting is sent from a browser.
 */
export const MAX_RECALLED_CONVERSATIONS: number = 5;

/**
 * How many past conversations are remembered before anyone has chosen.
 *
 * At the ceiling rather than at zero. The feature is worth nothing switched off, and the cap is
 * what bounds the storage - so the number to be careful about is the maximum, which is small, and
 * not the default.
 */
const DEFAULT_RECALLED_CONVERSATIONS: number = MAX_RECALLED_CONVERSATIONS;

/**
 * Whether opening the panel brings the last conversation back.
 *
 * Off by default: opening the assistant is far more often a new question than a continuation of
 * one from yesterday, and a panel that opens full of old text has to be read before it can be
 * used. Remembered per browser, like the panel's position.
 */
function storedResumePreference(): boolean {
    try {
        return window.localStorage.getItem(RESUME_STORAGE_KEY) === 'true';
    } catch {
        // Private browsing, or storage refused. The default is a fine answer.
        return false;
    }
}

/**
 * How many past conversations the assistant is set to remember.
 *
 * Clamped on the way out as well as on the way in, because a value that has been sitting in
 * localStorage since before the ceiling was what it is now would otherwise be trusted.
 */
function storedRecallPreference(): number {
    try {
        const raw: string | null = window.localStorage.getItem(RECALL_STORAGE_KEY);
        if (raw === null) {
            return DEFAULT_RECALLED_CONVERSATIONS;
        }
        const stored: number = Number.parseInt(raw, 10);
        if (Number.isNaN(stored)) {
            return DEFAULT_RECALLED_CONVERSATIONS;
        }
        return Math.max(0, Math.min(MAX_RECALLED_CONVERSATIONS, stored));
    } catch {
        return DEFAULT_RECALLED_CONVERSATIONS;
    }
}

/**
 * Which model the assistant is set to answer on.
 *
 * Checked against the offered list on the way out, not only on the way in. A selector stored by an
 * older build - or one that has since been taken off the menu - would otherwise be sent to Daive,
 * which would refuse it and quietly answer on its own default, so the header would name a model
 * that was not the one being billed.
 */
function storedModelPreference(): string {
    try {
        const stored: string | null = window.localStorage.getItem(MODEL_STORAGE_KEY);
        if (stored === null || !daiveChatModels.some((model): boolean => model.id === stored)) {
            return defaultDaiveChatModel;
        }
        return stored;
    } catch {
        return defaultDaiveChatModel;
    }
}

export const ChatBotContext: Context<any> = createContext(null);

class ChatBotContextProvider extends Component<any, ChatBotState> {

    /**
     * The form bridge, reached the way a class component reaches a context.
     *
     * This is rendered by the app bar, inside `AdminLayout`'s `ChatFormBridgeProvider`, so the
     * registry an edit screen publishes to is in scope here. A hook is not available: this is a
     * class, deliberately, because the streaming transcript needs the functional `setState` its
     * token handling relies on.
     */
    static contextType = ChatFormBridgeContext;
    declare context: ChatFormBridge | null;

    /**
     * Whether the next socket to open is a new conversation or the resumption of one.
     *
     * Opening the panel starts fresh: it forgets what was stored, so the transcript on screen and
     * what the assistant remembers are the same thing (user, 2026-09-08). A *reconnect* must not
     * do that - a socket that dropped mid-answer has to come back to the conversation it left.
     */
    private startFresh: boolean = false;

    private retryTimer: ReturnType<typeof setTimeout> | null = null;
    private idleTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(props: any) {
        super(props);
        this.state = {
            connection: null,
            chatMessages: [],
            websocket: null,
            thinking: false,
            activity: '',
            stopping: false,
            loadingOperator: true,
            showChatPanel: false,
            errorMessage: '',
            retries: 0,
            transcribing: false,
            transcript: null,
            conversations: 0,
            resumeConversation: storedResumePreference(),
            recallConversations: storedRecallPreference(),
            chatModel: storedModelPreference()
        };
    }

    componentDidMount(): void {
        // A reload or a closed tab does not unmount React, so componentWillUnmount never runs.
        // Both events are registered on purpose: `beforeunload` is what NokTestingTestingModal
        // uses and fires early enough in the unload sequence for the socket to be torn down,
        // and `pagehide` also covers the back/forward cache path, where `beforeunload` does not
        // fire at all. Whichever arrives first does the work; the other finds it already closed.
        window.addEventListener('beforeunload', this.handlePageHide);
        window.addEventListener('pagehide', this.handlePageHide);
    }

    componentWillUnmount(): void {
        window.removeEventListener('beforeunload', this.handlePageHide);
        window.removeEventListener('pagehide', this.handlePageHide);
        this.clearRetryTimer();
        this.clearIdleTimer();
        this.disconnectOperator();
    }

    private handlePageHide = (): void => {
        this.releaseSocket(true);
    };

    /**
     * Let go of the socket without the drop being treated as a fault.
     *
     * Clearing `onclose` first is the whole point: every deliberate close - the panel being shut,
     * the page unloading, the Operator saying it has finished - otherwise arrives at the same
     * handler as a dropped connection and starts the reconnect cycle. A page restored from the
     * back/forward cache used to come back with a retry already scheduled against a connection it
     * had given up.
     *
     * `sayGoodbye` sends '/stop' first, the same order `NokTestingTestingModal.disconnectOperator()`
     * uses. The command is what lets the Operator leave its receive loop deliberately instead of
     * waiting for the transport to drop, and it is not sent when the Operator is the side that
     * ended the conversation - there is nothing there to read it.
     */
    private releaseSocket(sayGoodbye: boolean): void {
        const socket: WebSocket | null = this.state.websocket;
        if (socket === null) {
            return;
        }
        socket.onclose = null;
        if (sayGoodbye && socket.readyState === WebSocket.OPEN) {
            socket.send('/stop');
        }
        socket.close();
    }

    private clearIdleTimer(): void {
        if (this.idleTimer !== null) {
            clearTimeout(this.idleTimer);
            this.idleTimer = null;
        }
    }

    /**
     * Hold a connection only while it is being used.
     *
     * An Operator has a finite number of slots, and a chat left open in a forgotten tab used to
     * hold one indefinitely.
     */
    private restartIdleTimer(): void {
        this.clearIdleTimer();
        this.idleTimer = setTimeout((): void => {
            this.disconnectOperator();
            this.setState({
                errorMessage: 'Disconnected after a period of inactivity. Close and reopen to continue.',
                loadingOperator: false,
            });
        }, IDLE_TIMEOUT_MS);
    }

    private clearRetryTimer(): void {
        if (this.retryTimer !== null) {
            clearTimeout(this.retryTimer);
            this.retryTimer = null;
        }
    }

    private authHeaders(): Headers {
        return new Headers({
            'Content-Type': 'application/json',
            'token': read_cookie('auth') as unknown as string
        });
    }

    /**
     * Add one streamed delta to the reply currently being written.
     *
     * This must use the functional form of setState: token frames arrive faster than React
     * re-renders, so a `[...this.state.chatMessages]` spread would read a stale array and lose
     * every delta that landed inside the same batch.
     */
    private appendToken(author: string, delta: string): void {
        if (delta === '') {
            return;
        }
        this.setState((prev: ChatBotState): ChatBotState => {
            const messages: ChatMessage[] = [...prev.chatMessages];
            const last: ChatMessage | undefined = messages[messages.length - 1];

            if (last !== undefined && last.type === 'operator' && last.streaming === true) {
                messages[messages.length - 1] = { ...last, message: last.message + delta };
            } else {
                messages.push({ message: delta, author: author, type: 'operator', streaming: true });
            }
            return { ...prev, chatMessages: messages, thinking: true, activity: 'answering', retries: 0 };
        });
    }

    /**
     * Put a stored conversation back on screen.
     *
     * Prepended rather than assigned: the frame answers the greeting this panel sent on opening,
     * and anything the user has managed to type in the meantime belongs after it, not instead of
     * it.
     *
     * Only what was said comes back. The Operator strips tool calls and their results before a
     * conversation is stored, so nothing here was *fetched* and a replayed transcript cannot put
     * stale data on screen.
     */
    private restoreTranscript(frame: OperatorFrame): void {
        const stored: ChatMessage[] = (frame.messages ?? [])
            .filter((entry): boolean => typeof entry?.message === 'string' && entry.message !== '')
            .map((entry): ChatMessage => ({
                message: entry.message,
                author: entry.author ?? '',
                type: entry.type === 'user' ? 'user' : 'operator'
            }));

        if (stored.length === 0) {
            return;
        }

        this.setState((prev: ChatBotState): ChatBotState => ({
            ...prev,
            retries: 0,
            chatMessages: [...stored, ...prev.chatMessages]
        }));
    }

    /**
     * Put a tool call into the transcript, and update it in place when it finishes.
     *
     * These sit inline between messages rather than in a separate panel, so the order in which
     * the assistant did things stays readable: it looked something up, then answered.
     */
    private recordToolActivity(frame: OperatorFrame): void {
        const id: string = frame.id ?? '';
        if (id === '') {
            return;
        }

        this.setState((prev: ChatBotState): ChatBotState => {
            const messages: ChatMessage[] = [...prev.chatMessages];
            const existing: number = messages.findIndex(
                (message: ChatMessage): boolean => message.type === 'tool' && message.tool?.id === id
            );

            if (existing === -1) {
                // HQ-11. A replayed chip belongs under the answer it came with, the same as
                // every other replayed card, so it goes through the same placement rather than
                // being pushed onto the end. A live one has no `afterTurn` and is appended.
                return {
                    ...prev,
                    retries: 0,
                    chatMessages: withCard(messages, {
                        message: '',
                        author: '',
                        type: 'tool',
                        tool: {
                            id: id,
                            name: frame.name ?? '',
                            label: frame.label ?? 'Working',
                            status: frame.status === 'done' ? 'done' : 'running',
                            summary: frame.summary,
                        }
                    }, frame.replayed === true ? frame.afterTurn : undefined),
                };
            } else {
                const previous: ChatToolActivity = messages[existing].tool as ChatToolActivity;
                messages[existing] = {
                    ...messages[existing],
                    tool: {
                        ...previous,
                        // A 'done' frame usually carries no label, and then the running one is
                        // kept. It carries one when the lookup learned the name of the record it
                        // was asked about, which is the one case where the chip that started the
                        // work is the chip still showing a number: following `accountHolder` on
                        // customer account 3 is what tells anybody it is Bob Bobberson's.
                        label: frame.label ?? previous.label,
                        status: frame.status === 'done' ? 'done' : previous.status,
                        summary: frame.summary ?? previous.summary,
                    }
                };
            }
            return { ...prev, chatMessages: messages, retries: 0 };
        });
    }

    /**
     * Put a map into the transcript for an address that was looked up.
     *
     * Sent by the Operator rather than written by the assistant. Asking the model to include a
     * map URL in its answer did not work: given a company's addresses it writes a tidy table and
     * drops the URLs, which is reasonable of it, and is why presentation is not its job.
     */
    private showMap(frame: OperatorFrame): void {
        if (!frame.embedUrl) {
            return;
        }
        // The same address can be looked up twice in one answer; show it once. Read from the
        // current state rather than inside the updater, because the insert itself now lives in
        // `addCard` and the check has to happen before it.
        const already: boolean = this.state.chatMessages.some(
            (message: ChatMessage): boolean => message.map?.embedUrl === frame.embedUrl
        );
        if (already) {
            return;
        }

        this.addCard({
            message: '',
            author: '',
            type: 'map',
            capturedAt: this.capturedAt(frame),
            map: {
                label: frame.label ?? 'Address',
                embedUrl: frame.embedUrl as string,
                hqUrl: frame.hqUrl ?? '',
            }
        }, frame);
    }

    /**
     * Put a card into the transcript, in the place it belongs.
     *
     * A live card goes on the end, which is where the conversation is. A replayed one carries
     * `afterTurn` - how many turns had been said when it was first drawn - so it goes back under
     * the answer it came with rather than into a heap of maps below the whole conversation.
     *
     * @param entry the card
     * @param frame the frame it came from
     */
    private addCard(entry: ChatMessage, frame: OperatorFrame): void {
        const afterTurn: number | undefined = frame.replayed === true ? frame.afterTurn : undefined;

        this.setState((prev: ChatBotState): ChatBotState => ({
            ...prev,
            retries: 0,
            chatMessages: withCard(prev.chatMessages, entry, afterTurn),
        }));
    }

    /**
     * When a frame was first drawn, if it is being shown again.
     *
     * HQ-11. One reader, so there is no card that replays without being dated: every entry that
     * carries `capturedAt` renders as a record of what was on screen, with its actions dead.
     *
     * @param frame the frame as it arrived
     * @return the capture time in unix seconds, or undefined for a live frame
     */
    private capturedAt(frame: OperatorFrame): number | undefined {
        return frame.replayed === true && typeof frame.capturedAt === 'number'
            ? frame.capturedAt
            : undefined;
    }

    /**
     * Put an offer to fill in a field into the transcript.
     *
     * An offer, never an edit. The card shows what the field holds beside what it would become,
     * and accepting writes it into the form on screen without saving - so the assistant stays
     * read-only and the change goes through the same validation and permissions as a typed value.
     */
    private offerFieldValue(frame: OperatorFrame): void {
        if (!frame.field) {
            return;
        }
        this.addCard({
            message: '',
            author: '',
            type: 'proposal',
            capturedAt: this.capturedAt(frame),
            proposal: {
                field: frame.field as string,
                value: frame.value ?? '',
                why: frame.why ?? '',
                resource: frame.resource ?? '',
                recordId: frame.recordId ?? null,
                was: frame.was ?? ''
            }
        }, frame);
    }

    /**
     * Put an offer to convert a sales contact into a customer into the transcript.
     *
     * An offer, never a conversion. The card shows what would be created, the admin corrects
     * anything wrong, and their press of Create is what reaches HQ-API. Nothing here writes, and
     * neither does Daive: see `ConversionCard` for who makes that request and with whose token.
     *
     * A frame with no email is dropped rather than drawn, because a card offering to create a
     * customer nobody can sign in as is worse than no card. Daive refuses the same case first.
     */
    private offerConversion(frame: OperatorFrame): void {
        if (!frame.customer?.email) {
            return;
        }
        this.addCard({
            message: '',
            author: '',
            type: 'conversion',
            capturedAt: this.capturedAt(frame),
            conversion: {
                sourceResource: frame.sourceResource ?? '',
                sourceId: Number(frame.sourceId ?? 0),
                sourceLabel: frame.sourceLabel ?? '',
                sourceHqUrl: frame.hqUrl ?? '',
                why: frame.why ?? '',
                customer: {
                    email: frame.customer.email ?? '',
                    firstName: frame.customer.firstName ?? '',
                    lastName: frame.customer.lastName ?? '',
                    telephone: frame.customer.telephone ?? '',
                    jobTitle: frame.customer.jobTitle ?? '',
                },
                account: {
                    type: frame.account?.type ?? 'company',
                    status: frame.account?.status ?? 'new',
                },
            }
        }, frame);
    }

    /**
     * Put an offer to open a record into the transcript.
     *
     * Deliberately an offer rather than a redirect. Moving somebody's view while they are still
     * reading is hostile, and it is a poor failure mode when the assistant has picked the wrong
     * record - which a button makes obvious and harmless.
     */
    private offerNavigation(frame: OperatorFrame): void {
        if (!frame.url) {
            return;
        }
        this.addCard({
            message: '',
            author: '',
            type: 'navigate',
            capturedAt: this.capturedAt(frame),
            navigation: {
                label: frame.label ?? 'Open in HQ',
                url: frame.url as string,
                resource: frame.resource ?? '',
            }
        }, frame);
    }

    /**
     * Put a report the assistant has written into the conversation.
     *
     * The frame carries the whole file, so this is the only place it exists. A frame with nothing
     * in it is dropped rather than shown as a card that downloads an empty document.
     */
    private offerReport(frame: OperatorFrame): void {
        // A replayed report carries no file: the bytes are not stored, so the card comes back
        // saying a report was produced rather than offering a download that cannot work.
        if (!frame.pdf && frame.replayed !== true) {
            return;
        }
        this.addCard({
            message: '',
            author: '',
            type: 'report',
            capturedAt: this.capturedAt(frame),
            report: {
                title: frame.title ?? 'Report',
                filename: frame.filename ?? 'report.pdf',
                bytes: Number(frame.bytes ?? 0),
                pdf: (frame.pdf ?? '') as string
            }
        }, frame);
    }

    toggleShowPanel(value: boolean): void {
        this.setState({ showChatPanel: value }, (): void => {
            if (value) {
                // The toggle decides. Reading it here rather than at connect time means a panel
                // opened while it was off starts clean even if it is switched on mid-session.
                this.startFresh = this.state.resumeConversation === false;
                this.loadChatConnection();
            } else {
                this.clearRetryTimer();
                this.disconnectOperator();
            }
        });
    }

    /**
     * Choose whether the last conversation comes back when the panel is opened.
     *
     * A toggle rather than a fixed rule, at the user's request: carrying a conversation across a
     * closed panel is what makes "and their phone number?" work an hour later, and it is also what
     * makes the panel open full of yesterday's text. Which of those matters depends on the day.
     */
    toggleResumeConversation(): void {
        this.setState((prev: ChatBotState): ChatBotState => {
            const resume: boolean = !prev.resumeConversation;
            try {
                window.localStorage.setItem(RESUME_STORAGE_KEY, String(resume));
            } catch {
                // Not being able to remember the preference is not a reason to refuse it now.
            }
            return { ...prev, resumeConversation: resume };
        });
    }

    /**
     * Choose how many finished conversations the assistant may be asked about.
     *
     * Zero switches it off, and Daive deletes what it was holding rather than merely hiding it -
     * a control that says how many conversations are remembered has to mean it when set to none.
     *
     * The number travels with each message rather than at connect time, so a change here takes
     * effect on the next thing that is said instead of on the next connection.
     *
     * @param wanted how many to remember, 0 to MAX_RECALLED_CONVERSATIONS
     */
    setRecallConversations(wanted: number): void {
        const chosen: number = Math.max(0, Math.min(MAX_RECALLED_CONVERSATIONS, wanted));
        try {
            window.localStorage.setItem(RECALL_STORAGE_KEY, String(chosen));
        } catch {
            // Not being able to remember the preference is not a reason to refuse it now.
        }
        this.setState((prev: ChatBotState): ChatBotState => ({ ...prev, recallConversations: chosen }));
    }

    /**
     * Choose which model answers.
     *
     * Travels with each message like the recall setting, so a change takes effect on the next
     * thing said rather than on the next connection. **The conversation is deliberately not
     * cleared.** All four are Mistral models reached through one provider, so the stored history
     * is as valid to the new one as to the old, and being made to start again to try a cheaper
     * model is exactly the friction that stops anyone trying.
     *
     * Ignored rather than stored when it is not something the menu offers. This is a browser
     * setting, and Daive clamps it again on arrival for the same reason.
     *
     * @param selector an LLM selector from `daiveChatModels`
     */
    setChatModel(selector: string): void {
        if (!daiveChatModels.some((model): boolean => model.id === selector)) {
            return;
        }
        try {
            window.localStorage.setItem(MODEL_STORAGE_KEY, selector);
        } catch {
            // Not being able to remember the preference is not a reason to refuse it now.
        }
        this.setState((prev: ChatBotState): ChatBotState => ({ ...prev, chatModel: selector }));
    }

    /**
     * Throw the conversation away and start again.
     *
     * The Operator keeps a conversation for fourteen days and replays it into the panel when it
     * reopens, which is what makes a follow-up like "and their phone number?" work across a
     * closed panel. This is the other half of that: a transcript you cannot clear is worse than
     * one that was never kept.
     */
    startNewConversation(): void {
        const socket: WebSocket | null = this.state.websocket;
        const connection: ChatConnection | null = this.state.connection;
        if (socket === null || connection === null || socket.readyState !== WebSocket.OPEN) {
            // Nothing to talk to, so at least clear what is on screen.
            this.setState({ chatMessages: [], errorMessage: '' });
            return;
        }

        try {
            socket.send(JSON.stringify({
                message: '/forget',
                type: 'chat',
                connectorId: connection.connectorId,
                adminUsername: connection.userName,
                botName: connection.botName,
                // Carried on the forget as well as on an ordinary message, because this is where
                // the conversation being thrown away is filed: Daive archives it on its way out,
                // and this number is how many it is allowed to keep.
                recall: this.state.recallConversations,
                model: this.state.chatModel
            }));
        } catch (error) {
            console.error('The conversation could not be cleared', error);
        }

        // Cleared here as well as on the frame coming back, so the panel responds to the click
        // rather than to the round trip. `conversations` rebuilds the composer, which is what
        // clears a half-typed question and any recording in progress - a reset has to leave this
        // looking like a connection that has never been used, not merely an empty transcript.
        this.setState((prev: ChatBotState): ChatBotState => ({
            ...prev,
            chatMessages: [],
            errorMessage: '',
            transcript: null,
            transcribing: false,
            thinking: true,
            activity: 'thinking',
            conversations: prev.conversations + 1
        }));
        this.restartIdleTimer();
    }

    /**
     * Ask HQ-API for a host and a signed ticket, then open a socket with it.
     *
     * Called for every connection including every reconnection, because a ticket is spent when it
     * is used and expires about a minute after it is issued. Reusing the stored one would be
     * refused by Daive as a replay.
     */
    loadChatConnection(): void {
        this.setState({ loadingOperator: true, errorMessage: '' });
        const url: string = UseUrlManagerCleanUrl({ url: hostUrl + 'hq/chat/operator' });

        fetch(new Request(url, { method: 'GET', headers: this.authHeaders() }))
            .then(async (response: Response): Promise<void> => {
                if (!response.ok) {
                    const body: { message?: string } = await response.json().catch(() => ({}));
                    this.setState({
                        loadingOperator: false,
                        errorMessage: body.message ?? 'Unable to reach the assistant right now.'
                    });
                    return;
                }
                const connection: ChatConnection = await response.json();
                this.setState({ connection: connection, retries: 0 }, (): void => {
                    this.createSocketToOperator(connection);
                });
            })
            .catch((error: Error): void => {
                console.error('Chat connection lookup failed', error);
                this.setState({
                    loadingOperator: false,
                    errorMessage: 'Unable to reach the assistant right now.'
                });
            });
    }

    createSocketToOperator(connection: ChatConnection | null): void {
        if (connection === null) {
            return;
        }

        // Hosts have always been stored with a trailing slash, which would otherwise produce
        // `wss://host//nn/chat/bot/ws`.
        const host: string = connection.host
            .replace('https://', 'wss://')
            .replace('http://', 'ws://')
            .replace(/\/+$/, '');
        const socketUrl: string = host + '/nn/chat/bot/ws';

        try {
            // The ticket rides in the subprotocol rather than the query string. A query string is
            // written to nginx's access log, kept in browser history and passed on by any proxy in
            // between; a subprotocol is a request header and is none of those things. Two names
            // are offered and Daive accepts the plain one, so the credential is not echoed back in
            // the response either.
            const socket: WebSocket = new WebSocket(socketUrl, [
                CHAT_SUBPROTOCOL,
                CHAT_TICKET_SUBPROTOCOL_PREFIX + connection.ticket
            ]);

            socket.onopen = (): void => {
                // Note that `retries` is deliberately NOT cleared here. Opening a socket proves
                // nothing: if the Operator accepts the connection and then fails on the
                // greeting, clearing the budget on every open turns the retry into an endless
                // connect/drop cycle against a server that cannot answer. Only a message
                // actually arriving clears it.
                this.setState({
                    websocket: socket,
                    loadingOperator: false,
                    errorMessage: ''
                }, (): void => {
                    this.restartIdleTimer();
                    const opening: boolean = this.startFresh;
                    this.startFresh = false;
                    // '/forget' clears the stored conversation and then greets; '/greeting' only
                    // greets, which is what a reconnect wants.
                    this.sendMessageToOperator(opening ? '/forget' : '/greeting');
                });
            };

            socket.onmessage = (event: MessageEvent<string>): void => {
                // Plain JSON. The Operator used to encrypt this and HQ never decrypted it.
                let frame: OperatorFrame;
                try {
                    frame = JSON.parse(event.data);
                } catch {
                    return;
                }

                // Something came back, so the connection demonstrably works. This is the only
                // place the retry budget is refilled - see the note in onopen.
                switch (frame.type) {
                    case 'token':
                        this.appendToken(connection.botName, frame.response ?? '');
                        break;
                    case 'status':
                        // Daive says which of the two it is doing. Both used to collapse into
                        // `thinking: true` and the distinction was thrown away, so the panel
                        // showed the same nothing whether it was waiting on the model or already
                        // being answered.
                        this.setState({
                            thinking: true,
                            activity: frame.status === 'answering' ? 'answering' : 'thinking',
                            retries: 0
                        });
                        break;
                    case 'tool':
                        this.recordToolActivity(frame);
                        break;
                    case 'navigate':
                        this.offerNavigation(frame);
                        break;
                    case 'map':
                        this.showMap(frame);
                        break;
                    case 'report':
                        this.offerReport(frame);
                        break;
                    case 'done':
                        this.setState((prev: ChatBotState): ChatBotState => ({
                            ...prev,
                            thinking: false,
                            activity: '',
                            stopping: false,
                            retries: 0,
                            chatMessages: prev.chatMessages.map(
                                (message: ChatMessage): ChatMessage =>
                                    message.streaming === true ? { ...message, streaming: false } : message
                            )
                        }));
                        break;
                    case 'history':
                        // The conversation this panel had last time, put back. Only sent when the
                        // toggle asked for it - see `resumeConversation`.
                        this.restoreTranscript(frame);
                        break;
                    case 'forgotten':
                        // The Operator has thrown the stored conversation away, so the panel
                        // clears what is on screen. Both halves, or reopening would replay a
                        // conversation the user had just asked to be rid of. The greeting that
                        // follows arrives after this, which is why `thinking` stays true.
                        this.setState({ chatMessages: [], errorMessage: '', retries: 0 });
                        break;
                    case 'proposal':
                        this.offerFieldValue(frame);
                        break;
                    case 'conversion':
                        this.offerConversion(frame);
                        break;
                    case 'transcript':
                        // Straight to the composer, never into the conversation. The stamp is
                        // what lets the same words be dictated twice and still arrive twice.
                        this.setState({
                            transcribing: false,
                            retries: 0,
                            transcript: { text: frame.text ?? '', at: Date.now() },
                            errorMessage: frame.error ?? ''
                        });
                        break;
                    case 'closed':
                        // The Operator let go first, and said why. Releasing the socket here
                        // rather than letting it drop is what makes the idle timeout stick: the
                        // close that follows this frame used to reach `onclose`, be read as a
                        // dropped connection, and reconnect - which took the slot straight back
                        // and gave the timeout nothing to do. The client's own timer normally
                        // fires first and hides that, but a throttled background tab is exactly
                        // the case the server timeout exists for.
                        this.clearIdleTimer();
                        this.releaseSocket(false);
                        this.setState({
                            websocket: null,
                            thinking: false,
                            activity: '',
                            loadingOperator: false,
                            errorMessage: frame.response ?? 'The assistant disconnected.',
                        });
                        break;
                    case 'error':
                        this.setState((prev: ChatBotState): ChatBotState => ({
                            ...prev,
                            thinking: false,
                            activity: '',
                            retries: 0,
                            errorMessage: frame.response ?? 'Something went wrong.'
                        }));
                        break;
                    default:
                        break;
                }
            };

            socket.onclose = (): void => {
                this.setState({ websocket: null, thinking: false, activity: '' });
                this.scheduleReconnect();
            };

            socket.onerror = (event: Event): void => {
                console.error('Chat socket error', event);
            };
        } catch (error) {
            console.error('Chat socket could not be opened', error);
            this.scheduleReconnect();
        }
    }

    /**
     * Retry a dropped socket, backing off, and stop when the attempts run out.
     *
     * The loop this replaces was `while (retry < retries || this.state.errorMessage === '')`, so
     * the attempt count could never end it: while no error had been recorded the right-hand side
     * stayed true, and recording an error is what the loop was counting towards.
     */
    private scheduleReconnect(): void {
        if (this.state.showChatPanel === false || this.state.connection === null) {
            return;
        }

        const attempt: number = this.state.retries + 1;
        if (attempt > MAX_RETRIES) {
            this.setState({
                errorMessage: 'Lost contact with ' + this.state.connection.botName + '. Close and reopen to try again.',
                loadingOperator: false,
                retries: 0
            });
            return;
        }

        this.setState({ retries: attempt, loadingOperator: true });
        this.clearRetryTimer();
        this.retryTimer = setTimeout((): void => {
            // A fresh connection rather than the stored one. The ticket on it has been spent and
            // has almost certainly expired, so reopening with it would be refused as a replay -
            // and the retry would fail for that reason rather than whatever dropped the socket.
            this.loadChatConnection();
        }, RETRY_BACKOFF_MS * attempt);
    }

    /**
     * Send a recording from the composer for transcription.
     *
     * Over the chat socket rather than a request of its own: that connection is already open and
     * already authenticated, so this needs no second route, no CORS surface and no token in the
     * browser that was not there before.
     */
    sendRecordingToOperator(audio: string, mime: string): void {
        const socket: WebSocket | null = this.state.websocket;
        if (socket === null || socket.readyState !== WebSocket.OPEN || audio === '') {
            return;
        }

        try {
            socket.send(JSON.stringify({ type: 'transcribe', audio: audio, mime: mime }));
        } catch (error) {
            console.error('Recording could not be sent', error);
            return;
        }

        this.restartIdleTimer();
        this.setState({ transcribing: true, errorMessage: '' });
    }

    sendMessageToOperator(message: string): void {
        const socket: WebSocket | null = this.state.websocket;
        const connection: ChatConnection | null = this.state.connection;
        if (socket === null || connection === null || socket.readyState !== WebSocket.OPEN) {
            return;
        }

        // Read at send time, not held: the admin navigates mid-conversation, and a screen
        // described when the panel opened would be the wrong one by the second question.
        const screen: ChatScreen | null = describeScreen(this.context);

        try {
            socket.send(JSON.stringify({
                message: message,
                type: 'chat',
                connectorId: connection.connectorId,
                adminUsername: connection.userName,
                botName: connection.botName,
                screen: screen,
                // Read at send time like the screen, so turning the number up or down takes
                // effect on the next thing said rather than on the next connection.
                recall: this.state.recallConversations,
                // Same again. A greeting costs nothing and reaches no model, so this only ever
                // matters on a real question.
                model: this.state.chatModel
            }));
        } catch (error) {
            console.error('Chat message could not be sent', error);
            return;
        }

        this.restartIdleTimer();
        this.setState((prev: ChatBotState): ChatBotState => ({
            ...prev,
            thinking: true,
            // Set here rather than waiting for Daive's status frame, so the indicator answers the
            // click rather than the round trip. The frame that follows only confirms it.
            activity: 'thinking',
            // A Stop still waiting when the socket dropped would otherwise disable the next one.
            stopping: false,
            errorMessage: '',
            chatMessages: message === '/greeting' || message === '/forget'
                ? prev.chatMessages
                : [...prev.chatMessages, {
                    message: message,
                    author: connection.userName,
                    type: 'user'
                }]
        }));
    }

    /**
     * Ask Daive to stop the reply it is writing (DAI-07).
     *
     * Only asks. Daive ends the reply with a "Stopped." line and the usual `done`, which is what
     * closes the bubble and frees the composer, so nothing here pretends the reply has ended. A
     * Stop that arrives after the reply finished is ignored there.
     */
    stopReply(): void {
        const socket: WebSocket | null = this.state.websocket;
        if (socket === null || socket.readyState !== WebSocket.OPEN || !this.state.thinking || this.state.stopping) {
            return;
        }

        try {
            socket.send(JSON.stringify({ type: 'cancel' }));
        } catch (error) {
            console.error('Stop could not be sent', error);
            return;
        }

        this.setState({ stopping: true });
    }

    disconnectOperator(): void {
        this.clearIdleTimer();
        this.releaseSocket(true);
        this.setState({
            connection: null,
            websocket: null,
            chatMessages: [],
            thinking: false,
            activity: '',
            stopping: false,
            loadingOperator: true,
            errorMessage: '',
            retries: 0,
            transcribing: false,
            transcript: null,
            conversations: 0
            // `resumeConversation`, `recallConversations` and `chatModel` are deliberately
            // absent: this is an object setState, so it merges, and all three preferences must
            // survive the panel being closed.
        });
    }

    render(): JSX.Element {
        return (
            <ChatBotContext.Provider value={{
                ...this.state,
                createSocketToOperator: this.createSocketToOperator.bind(this),
                disconnectOperator: this.disconnectOperator.bind(this),
                sendMessageToOperator: this.sendMessageToOperator.bind(this),
                sendRecordingToOperator: this.sendRecordingToOperator.bind(this),
                stopReply: this.stopReply.bind(this),
                startNewConversation: this.startNewConversation.bind(this),
                setRecallConversations: this.setRecallConversations.bind(this),
                setChatModel: this.setChatModel.bind(this),
                toggleResumeConversation: this.toggleResumeConversation.bind(this),
                toggleShowPanel: this.toggleShowPanel.bind(this)
            }}>
                { this.props.children }
            </ChatBotContext.Provider>
        );
    }
}

export default ChatBotContextProvider;
